import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Heading, Text, VStack, HStack, Switch, Checkbox, FormControl, FormLabel, useToast } from '@chakra-ui/react';
import apiClient from '../lib/api/apiClient';
import { useUserStore } from '../store/userStore';
import { fetchUserSettings } from '../hooks/useUser';

interface NotificationPreference {
  enabled: boolean;
  sms: boolean;
  email: boolean;
}

interface UserSettings {
  general_email: boolean;
  general_sms: boolean;
  team_management_email: boolean;
  team_management_sms: boolean;
  trade_email: boolean;
  trade_sms: boolean;
  waiver_fa_email: boolean;
  waiver_fa_sms: boolean;
  two_fa_enabled: boolean;
  user_id: number;
}

const NotificationsTab: React.FC = () => {
  const { user } = useUserStore();
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const toast = useToast();

  // Initialize preferences from userSettings
  const [preferences, setPreferences] = useState<Record<string, NotificationPreference>>({
    trades: { enabled: false, sms: false, email: false },
    waivers: { enabled: false, sms: false, email: false },
    general: { enabled: false, sms: false, email: false },
    teamManagement: { enabled: false, sms: false, email: false },
    account: { enabled: false, sms: false, email: false }
  });

  // Fetch user settings on component mount
  useEffect(() => {
    const loadUserSettings = async () => {
      if (user?.id) {
        setIsLoadingSettings(true);
        try {
          const settings = await fetchUserSettings(user.id);
          setUserSettings(settings);
          console.log('Loaded user settings:', settings);
        } catch (error) {
          console.error('Error loading user settings:', error);
        } finally {
          setIsLoadingSettings(false);
        }
      }
    };

    loadUserSettings();
  }, [user?.id]);

  // Update preferences when userSettings are loaded
  useEffect(() => {
    if (userSettings) {
      console.log('Initializing preferences from userSettings');
      
      const newPreferences = {
        trades: {
          enabled: Boolean(userSettings.trade_email || userSettings.trade_sms),
          email: Boolean(userSettings.trade_email),
          sms: Boolean(userSettings.trade_sms)
        },
        waivers: {
          enabled: Boolean(userSettings.waiver_fa_email || userSettings.waiver_fa_sms),
          email: Boolean(userSettings.waiver_fa_email),
          sms: Boolean(userSettings.waiver_fa_sms)
        },
        general: {
          enabled: Boolean(userSettings.general_email || userSettings.general_sms),
          email: Boolean(userSettings.general_email),
          sms: Boolean(userSettings.general_sms)
        },
        teamManagement: {
          enabled: Boolean(userSettings.team_management_email || userSettings.team_management_sms),
          email: Boolean(userSettings.team_management_email),
          sms: Boolean(userSettings.team_management_sms)
        },
        account: {
          enabled: false, // Account notifications are disabled by default
          email: false,
          sms: false
        }
      };
      
      console.log('New preferences:', newPreferences);
      setPreferences(newPreferences);
    }
  }, [userSettings]);

  // Debounce timer for saving preferences
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  // Convert preferences to API format
  const convertToApiFormat = (prefs: Record<string, NotificationPreference>) => {
    return {
      trade_email: prefs.trades.email,
      trade_sms: prefs.trades.sms,
      waiver_fa_email: prefs.waivers.email,
      waiver_fa_sms: prefs.waivers.sms,
      general_email: prefs.general.email,
      general_sms: prefs.general.sms,
      team_management_email: prefs.teamManagement.email,
      team_management_sms: prefs.teamManagement.sms
    };
  };

  // Save preferences to API
  const savePreferences = useCallback(async (prefs: Record<string, NotificationPreference>) => {
    try {
      setIsSaving(true);
      const apiData = convertToApiFormat(prefs);
      const response = await apiClient.post('/user/notification-preferences', apiData);

      if (response.status !== 200) {
        throw new Error(`Failed to save preferences: ${response.statusText}`);
      }

      setHasUnsavedChanges(false);
      toast({
        title: 'Preferences saved',
        description: 'Your notification preferences have been updated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: 'Save failed',
        description: 'Failed to save notification preferences. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [toast]);

  // Debounced save function
  const debouncedSave = useCallback((prefs: Record<string, NotificationPreference>) => {
    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Set new timeout for 3 seconds
    const newTimeout = setTimeout(() => {
      savePreferences(prefs);
    }, 3000);

    setSaveTimeout(newTimeout);
  }, [saveTimeout, savePreferences]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout);
      }
    };
  }, [saveTimeout]);

  const handleCategoryToggle = (key: string, enabled: boolean) => {
    const newPreferences = {
      ...preferences,
      [key]: { 
        ...preferences[key], 
        enabled,
        // If disabling, also disable both SMS and email
        sms: enabled ? preferences[key].sms : false,
        email: enabled ? preferences[key].email : false
      }
    };
    setPreferences(newPreferences);
    setHasUnsavedChanges(true);
    debouncedSave(newPreferences);
  };

  const handleMethodToggle = (key: string, method: 'sms' | 'email', value: boolean) => {
    const newPreferences = {
      ...preferences,
      [key]: { ...preferences[key], [method]: value }
    };
    setPreferences(newPreferences);
    setHasUnsavedChanges(true);
    debouncedSave(newPreferences);
  };

  // Debug: Log current preferences state
  console.log('Current preferences state:', preferences);
  
  // Show loading state while fetching settings
  if (isLoadingSettings) {
    return (
      <Box maxW="800px" mx="auto" mt={4}>
        <Heading size="md" mb={6} color="white">Notification Preferences</Heading>
        <Text color="gray.300" textAlign="center" py={8}>Loading your notification preferences...</Text>
      </Box>
    );
  }
  
  return (
    <Box maxW="800px" mx="auto" mt={4}>
      <Heading size="md" mb={6} color="white">Notification Preferences</Heading>
      <Text color="gray.300" mb={6}>Configure how you receive notifications from Dominus Baseball.</Text>
      
      {/* Save Status Indicator */}
      {hasUnsavedChanges && (
        <Box bg="blue.500" color="white" p={3} borderRadius="md" mb={4} textAlign="center" maxW="200px" mx="auto">
          {isSaving ? 'Saving...' : 'Save my Changes'}
        </Box>
      )}
      
      <VStack spacing={8} align="stretch">
        {/* Trades */}
        <Box bg="whiteAlpha.50" p={6} borderRadius="lg" border="1px solid" borderColor="whiteAlpha.200">
          <FormControl>
            <FormLabel color="white" fontWeight="bold" fontSize="lg" mb={4}>
              Trades
            </FormLabel>
            
            <VStack spacing={4} align="stretch">
              <Checkbox 
                colorScheme="blue" 
                isChecked={preferences.trades.enabled}
                onChange={(e) => handleCategoryToggle('trades', e.target.checked)}
                color="white"
              >
                Enable notifications
              </Checkbox>
              
              {preferences.trades.enabled && (
                <>
                  <HStack justify="space-between">
                    <Text color="gray.300">Email notifications</Text>
                    <Switch 
                      colorScheme="blue" 
                      isChecked={preferences.trades.email}
                      onChange={(e) => handleMethodToggle('trades', 'email', e.target.checked)}
                    />
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="gray.300">SMS notifications</Text>
                    <Switch 
                      colorScheme="blue" 
                      isChecked={preferences.trades.sms}
                      onChange={(e) => handleMethodToggle('trades', 'sms', e.target.checked)}
                    />
                  </HStack>
                </>
              )}
            </VStack>
          </FormControl>
        </Box>

        {/* Waivers and Free Agent Moves */}
        <Box bg="whiteAlpha.50" p={6} borderRadius="lg" border="1px solid" borderColor="whiteAlpha.200">
          <FormControl>
            <FormLabel color="white" fontWeight="bold" fontSize="lg" mb={4}>
              Waivers and Free Agent Moves
            </FormLabel>
            
            <VStack spacing={4} align="stretch">
              <Checkbox 
                colorScheme="blue" 
                isChecked={preferences.waivers.enabled}
                onChange={(e) => handleCategoryToggle('waivers', e.target.checked)}
                color="white"
              >   
                Enable notifications
              </Checkbox>
              
              {preferences.waivers.enabled && (
                <>
                  <HStack justify="space-between">
                    <Text color="gray.300">Email notifications</Text>
                    <Switch 
                      colorScheme="blue" 
                      isChecked={preferences.waivers.email}
                      onChange={(e) => handleMethodToggle('waivers', 'email', e.target.checked)}
                    />
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="gray.300">SMS notifications</Text>
                    <Switch 
                      colorScheme="blue" 
                      isChecked={preferences.waivers.sms}
                      onChange={(e) => handleMethodToggle('waivers', 'sms', e.target.checked)}
                    />
                  </HStack>
                </>
              )}
            </VStack>
          </FormControl>
        </Box>

        {/* General */}
        <Box bg="whiteAlpha.50" p={6} borderRadius="lg" border="1px solid" borderColor="whiteAlpha.200">
          <FormControl>
            <FormLabel color="white" fontWeight="bold" fontSize="lg" mb={4}>
              General
            </FormLabel>
            
            <VStack spacing={4} align="stretch">
              <Checkbox 
                colorScheme="blue" 
                isChecked={preferences.general.enabled}
                onChange={(e) => handleCategoryToggle('general', e.target.checked)}
                color="white"
              >
                Enable notifications
              </Checkbox>
              
              {preferences.general.enabled && (
                <>
                  <HStack justify="space-between">
                    <Text color="gray.300">Email notifications</Text>
                    <Switch 
                      colorScheme="blue" 
                      isChecked={preferences.general.email}
                      onChange={(e) => handleMethodToggle('general', 'email', e.target.checked)}
                    />
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="gray.300">SMS notifications</Text>
                    <Switch 
                      colorScheme="blue" 
                      isChecked={preferences.general.sms}
                      onChange={(e) => handleMethodToggle('general', 'sms', e.target.checked)}
                    />
                  </HStack>
                </>
              )}
            </VStack>
          </FormControl>
        </Box>

        {/* Team Management */}
        <Box bg="whiteAlpha.50" p={6} borderRadius="lg" border="1px solid" borderColor="whiteAlpha.200">
          <FormControl>
            <FormLabel color="white" fontWeight="bold" fontSize="lg" mb={4}>
              Team Management
            </FormLabel>
            
            <VStack spacing={4} align="stretch">
              <Checkbox 
                colorScheme="blue" 
                isChecked={preferences.teamManagement.enabled}
                onChange={(e) => handleCategoryToggle('teamManagement', e.target.checked)}
                color="white"
              >
                Enable notifications
              </Checkbox>
              
              {preferences.teamManagement.enabled && (
                <>
                  <HStack justify="space-between">
                    <Text color="gray.300">Email notifications</Text>
                    <Switch 
                      colorScheme="blue" 
                      isChecked={preferences.teamManagement.email}
                      onChange={(e) => handleMethodToggle('teamManagement', 'email', e.target.checked)}
                    />
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="gray.300">SMS notifications</Text>
                    <Switch 
                      colorScheme="blue" 
                      isChecked={preferences.teamManagement.sms}
                      onChange={(e) => handleMethodToggle('teamManagement', 'sms', e.target.checked)}
                    />
                  </HStack>
                </>
              )}
            </VStack>
          </FormControl>
        </Box>

        {/* Account */}
        <Box bg="whiteAlpha.50" p={6} borderRadius="lg" border="1px solid" borderColor="whiteAlpha.200">
          <FormControl>
            <FormLabel color="white" fontWeight="bold" fontSize="lg" mb={4}>
              Account
            </FormLabel>
            
            <VStack spacing={4} align="stretch">
              <Checkbox 
                colorScheme="blue" 
                isChecked={true}
                onChange={(e) => handleCategoryToggle('account', e.target.checked)}
                isDisabled
                color="white"
              >
                Enable notifications
              </Checkbox>
              
              {preferences.account.enabled && (
                <>
                  <HStack justify="space-between">
                    <Text color="gray.300">Email notifications</Text>
                    <Switch 
                      colorScheme="blue" 
                      isChecked={preferences.account.email}
                      onChange={(e) => handleMethodToggle('account', 'email', e.target.checked)}
                    />
                  </HStack>
                  <HStack justify="space-between">
                    <Text color="gray.300">SMS notifications</Text>
                    <Switch 
                      colorScheme="blue" 
                      isChecked={preferences.account.sms}
                      onChange={(e) => handleMethodToggle('account', 'sms', e.target.checked)}
                    />
                  </HStack>
                </>
              )}
            </VStack>
          </FormControl>
        </Box>
      </VStack>
    </Box>
  );
};

export default NotificationsTab;
